// Merchant contacts (FIG-345 · UC-228), stored in public.organisation_contacts.
// Shared by Settings › Merchant Contacts and the manage-merchant-contacts-v1
// edge function, so it has no imports.

export interface MerchantContact {
  id: string;
  organisation_id: string;
  name: string;
  role: string | null;
  email: string;
  phone: string | null;
  slack: string | null;
  is_poc: boolean;
  created_at: string;
  updated_at: string;
}

/** What an admin types for a contact. Empty optional fields are saved as null. */
export interface MerchantContactInput {
  name: string;
  role?: string | null;
  email: string;
  phone?: string | null;
  slack?: string | null;
}

export type MerchantContactField = keyof MerchantContactInput;

export const MERCHANT_CONTACT_MAX_LENGTH: Record<MerchantContactField, number> = {
  name: 200,
  role: 120,
  email: 320,
  phone: 32,
  slack: 120,
};

export const MERCHANT_CONTACT_LABELS: Record<MerchantContactField, string> = {
  name: 'Name',
  role: 'Role',
  email: 'Email',
  phone: 'Phone',
  slack: 'Slack',
};

/** Same pattern as src/utils/validation.ts isValidEmail. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Same rules as src/utils/phoneValidation.ts validatePhoneNumber (FIG-319):
 * a + country code that doesn't start with 0, then 7–15 digits in all.
 */
const PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

export const cleanPhone = (value: string): string => value.replace(/[^\d+]/g, '');

const trimOrNull = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : null;
};

export interface NormalisedContactInput {
  name: string;
  role: string | null;
  email: string;
  phone: string | null;
  slack: string | null;
}

/** Trimmed values as they are saved; the phone keeps only + and digits. */
export function normaliseContactInput(input: MerchantContactInput): NormalisedContactInput {
  const phone = trimOrNull(input.phone);
  return {
    name: (input.name ?? '').trim(),
    role: trimOrNull(input.role),
    email: (input.email ?? '').trim(),
    phone: phone ? cleanPhone(phone) : null,
    slack: trimOrNull(input.slack),
  };
}

/** Inline errors for the contact form; an empty object means it can be saved. */
export function contactFieldErrors(input: MerchantContactInput): Partial<Record<MerchantContactField, string>> {
  const value = normaliseContactInput(input);
  const errors: Partial<Record<MerchantContactField, string>> = {};
  if (!value.name) errors.name = 'Name is required';
  if (!value.email) errors.email = 'Email is required';
  else if (!EMAIL_PATTERN.test(value.email)) errors.email = 'Enter a valid email';
  if (value.phone && !PHONE_PATTERN.test(value.phone)) {
    errors.phone = 'Include the country code, for example +14155550123';
  }
  (Object.keys(MERCHANT_CONTACT_MAX_LENGTH) as MerchantContactField[]).forEach((field) => {
    const text = value[field];
    if (!errors[field] && text && text.length > MERCHANT_CONTACT_MAX_LENGTH[field]) {
      errors[field] = `${MERCHANT_CONTACT_LABELS[field]} is too long`;
    }
  });
  return errors;
}

/** POC first, then by name (case-insensitive), then email. */
export function sortMerchantContacts<T extends Pick<MerchantContact, 'is_poc' | 'name' | 'email'>>(contacts: T[]): T[] {
  return [...contacts].sort((a, b) => {
    if (a.is_poc !== b.is_poc) return a.is_poc ? -1 : 1;
    const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    return byName !== 0 ? byName : a.email.localeCompare(b.email, undefined, { sensitivity: 'base' });
  });
}

export const sameEmail = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** Result of manage-merchant-contacts-v1. */
export interface ManageMerchantContactsResponse {
  success: boolean;
  error?: string;
  /** Per-field errors for a 400 from add or edit. */
  fieldErrors?: Partial<Record<MerchantContactField, string>>;
  contacts?: MerchantContact[];
  /** The change saved, but the list couldn't be reloaded. */
  reloadFailed?: boolean;
  /** False when the change was saved but its history entry wasn't written. */
  recorded?: boolean;
  /** Set when the sign-in email changed: whether the magic link was emailed. */
  linkSent?: boolean;
  /** The address the magic link was (or should have been) sent to. */
  linkEmail?: string;
}
